// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext(null);

async function syncLocalToCloud() {
  try {
    const favorites = JSON.parse(localStorage.getItem('puvio_favorites') || '[]');
    const visited = JSON.parse(localStorage.getItem('gt_visited') || '[]');
    const diaryRaw = localStorage.getItem('gt_diary');
    let diary = [];
    if (diaryRaw) {
      try {
        const parsed = JSON.parse(diaryRaw);
        if (Array.isArray(parsed)) diary = parsed;
      } catch {}
    }
    if (!favorites.length && !visited.length && !diary.length) return;
    await fetch(`${API}/api/user/sync-from-local`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ favorites, visited, diary }),
    });
    localStorage.setItem('gt_synced_once', '1');
  } catch (e) {
    /* non-fatal */
  }
}

/** Pull cloud items down + merge into localStorage so a signed-in user
 *  who logs in on a fresh device sees everything they've saved elsewhere. */
async function downloadCloudToLocal() {
  try {
    const [favRes, visRes, diaRes] = await Promise.allSettled([
      fetch(`${API}/api/user/favorites`, { credentials: 'include' }),
      fetch(`${API}/api/user/visited`,   { credentials: 'include' }),
      fetch(`${API}/api/user/diary`,     { credentials: 'include' }),
    ]);
    // Helper: merge by stable key, keeping latest by ts when available
    const mergeArr = (localArr, cloudArr, keyFn) => {
      const m = new Map();
      [...(localArr || []), ...(cloudArr || [])].forEach(x => {
        const k = keyFn(x);
        if (!m.has(k)) m.set(k, x);
        else {
          const a = m.get(k);
          const aTs = a?.ts || a?.created_at || '';
          const bTs = x?.ts || x?.created_at || '';
          if (bTs > aTs) m.set(k, x);
        }
      });
      return Array.from(m.values());
    };

    if (favRes.status === 'fulfilled' && favRes.value.ok) {
      const cloud = (await favRes.value.json())?.items || [];
      const local = JSON.parse(localStorage.getItem('puvio_favorites') || '[]');
      const merged = mergeArr(local, cloud, x => x?.place_id || `${x?.name}|${x?.lat}|${x?.lon}`);
      localStorage.setItem('puvio_favorites', JSON.stringify(merged));
    }
    if (visRes.status === 'fulfilled' && visRes.value.ok) {
      const cloud = (await visRes.value.json())?.items || [];
      const local = JSON.parse(localStorage.getItem('gt_visited') || '[]');
      const merged = mergeArr(local, cloud, x => `${x?.name || ''}|${x?.country || ''}`);
      localStorage.setItem('gt_visited', JSON.stringify(merged.slice(-100)));
    }
    if (diaRes.status === 'fulfilled' && diaRes.value.ok) {
      const cloud = (await diaRes.value.json())?.items || [];
      let local = [];
      try {
        const parsed = JSON.parse(localStorage.getItem('gt_diary') || '[]');
        if (Array.isArray(parsed)) local = parsed;
      } catch {}
      const merged = mergeArr(local, cloud, x => x?.id || `${x?.city_name || ''}|${x?.created_at || x?.ts || ''}`);
      localStorage.setItem('gt_diary', JSON.stringify(merged));
    }
    // Notify any open page so it can re-read localStorage
    window.dispatchEvent(new Event('trivly:cloud-sync'));
  } catch (e) {
    /* non-fatal */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (r.ok) {
        const u = await r.json();
        setUser(u);
        // Already-signed-in tab: also pull cloud → local so a new device gets prior data.
        downloadCloudToLocal();
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip /me — AuthCallback will run first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/profile';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    setUser(null);
    localStorage.removeItem('gt_synced_once');
  }, []);

  const completeSession = useCallback(async (sessionId) => {
    const r = await fetch(`${API}/api/auth/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!r.ok) throw new Error('Auth exchange failed');
    const data = await r.json();
    setUser(data.user);
    if (!localStorage.getItem('gt_synced_once')) {
      // Upload local items first so the cloud has everything, then pull cloud back
      // (in case other devices added items we don't have locally).
      await syncLocalToCloud();
    }
    await downloadCloudToLocal();
    return data.user;
  }, []);

  /** Email + password — POST /api/auth/email/login. Server sets the same
   *  session_token httpOnly cookie so /api/auth/me + /api/user/* work
   *  identically for Google, phone-OTP, and email-password providers. */
  const loginWithEmail = useCallback(async (email, password) => {
    const r = await fetch(`${API}/api/auth/email/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d?.msg || '').join(' ') : 'Sign in failed');
      throw new Error(msg);
    }
    setUser(data.user);
    if (!localStorage.getItem('gt_synced_once')) {
      await syncLocalToCloud();
    }
    await downloadCloudToLocal();
    return data.user;
  }, []);

  const registerWithEmail = useCallback(async (email, password, name) => {
    const r = await fetch(`${API}/api/auth/email/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d?.msg || '').join(' ') : 'Sign up failed');
      throw new Error(msg);
    }
    setUser(data.user);
    if (!localStorage.getItem('gt_synced_once')) {
      await syncLocalToCloud();
    }
    await downloadCloudToLocal();
    return data.user;
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, login, logout, completeSession, loginWithEmail, registerWithEmail, refresh: checkAuth }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
